import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { VideoCamera, Sparkle, Download, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { debugLog } from "../lib/debug";
import { useAuth } from "../context/AuthContext";

const SIZES = [
  { id: "720x1280", label: "9:16 Vertical" },
  { id: "1280x720", label: "16:9 HD" },
];

export default function BrollStudio() {
  const { setCredits } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState("720x1280");
  const [duration, setDuration] = useState(4);
  const [model, setModel] = useState("sora-2");
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = async () => {
    try {
      const { data } = await api.get("/ai/broll");
      setJobs(data);
    } catch (e) {
      debugLog("broll jobs load failed:", e?.message);
    }
  };

  useEffect(() => {
    loadJobs();
    pollRef.current = setInterval(loadJobs, 8000);
    return () => clearInterval(pollRef.current);
  }, []);

  const submit = async () => {
    if (!prompt.trim()) { toast.error("Describe your B-roll"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ai/broll", { prompt, size, duration, model });
      if (typeof data?._credits_remaining === "number") setCredits(data._credits_remaining);
      toast.success("Queued — Sora 2 typically takes 2-5 minutes");
      loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to queue");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="broll-studio">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[#FF3B6B] text-sm font-mono">// SORA 2 B-ROLL</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Generate cinematic B-roll</h1>
          <p className="text-white/50 mt-1">Powered by Sora 2 · 4-12 second clips · async generation · 15 credits/clip</p>
        </div>
        <Link to="/app/broll-history" className="glass rounded-lg px-3 py-2 text-sm flex items-center gap-2 hover:bg-white/5" data-testid="broll-history-link">
          History
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 glass rounded-xl p-6 space-y-5">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Describe the scene</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} placeholder="e.g. close-up of hands typing on laptop in dim coffee shop, cinematic, warm lighting" className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:border-[#FF3B6B] focus:outline-none resize-none" data-testid="broll-prompt-input" />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Aspect ratio</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {SIZES.map(s => (
                <button key={s.id} onClick={() => setSize(s.id)} className={`py-2 rounded-md text-xs ${size === s.id ? "bg-[#FF3B6B]" : "bg-white/5 border border-white/10"}`} data-testid={`broll-size-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wider">Duration</label>
              <div className="mt-2 grid grid-cols-3 gap-1">
                {[4, 8, 12].map(d => (
                  <button key={d} onClick={() => setDuration(d)} className={`py-2 rounded-md text-xs ${duration === d ? "bg-[#FF3B6B]" : "bg-white/5 border border-white/10"}`} data-testid={`broll-dur-${d}`}>{d}s</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wider">Quality</label>
              <div className="mt-2 grid grid-cols-2 gap-1">
                {["sora-2", "sora-2-pro"].map(m => (
                  <button key={m} onClick={() => setModel(m)} className={`py-2 rounded-md text-xs ${model === m ? "bg-[#FF3B6B]" : "bg-white/5 border border-white/10"}`} data-testid={`broll-model-${m}`}>
                    {m === "sora-2" ? "Standard" : "Pro"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button onClick={submit} disabled={submitting} className="w-full btn-primary py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-broll-btn">
            {submitting ? "Queuing..." : <><Sparkle weight="fill" size={16} /> Generate B-roll</>}
          </button>
          <p className="text-xs text-white/40">~2-5 minutes per clip. We'll notify you when done.</p>
        </div>

        <div className="lg:col-span-7 space-y-3">
          <h2 className="text-sm font-semibold text-white/70 flex items-center gap-2">
            <Clock size={14} /> Your B-roll library ({jobs.length})
          </h2>
          {jobs.length === 0 && (
            <div className="glass rounded-xl p-12 text-center text-white/40 border-dashed">
              <VideoCamera size={50} className="mx-auto mb-3 text-white/20" />
              <p>Your generated clips will appear here</p>
            </div>
          )}
          {jobs.map(j => (
            <div key={j.id} className="glass rounded-xl p-4" data-testid={`broll-job-${j.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{j.prompt}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-white/50">
                    <span>{j.size}</span><span>·</span><span>{j.duration}s</span><span>·</span><span>{j.model}</span>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-mono uppercase ${
                  j.status === "done" ? "bg-[#00E5FF]/10 text-[#00E5FF]" :
                  j.status === "failed" ? "bg-red-500/10 text-red-400" :
                  "bg-[#EAB308]/10 text-[#EAB308]"
                }`}>{j.status}</span>
              </div>
              {j.status === "done" && j.storage_path && (
                <div className="mt-3">
                  <BrollVideo job={j} />
                </div>
              )}
              {j.status === "failed" && j.error && (
                <p className="mt-2 text-xs text-red-400">{j.error}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BrollVideo({ job }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let url = null;
    (async () => {
      try {
        const { data: signed } = await api.get(`/ai/broll/${job.id}/sign`);
        const resp = await fetch(`${API}/ai/broll/${job.id}/file?sig=${encodeURIComponent(signed.sig)}`);
        if (!resp.ok) return;
        const blob = await resp.blob();
        url = URL.createObjectURL(blob);
        if (alive) setBlobUrl(url);
      } catch (e) {
        debugLog("broll signed fetch failed:", e?.message);
      }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [job.id]);

  if (!blobUrl) return <div className="h-32 shimmer bg-white/5 rounded" />;
  return (
    <div className="space-y-2">
      <video src={blobUrl} controls className="w-full rounded-lg max-h-80" />
      <a href={blobUrl} download={`broll-${job.id}.mp4`} className="inline-flex items-center gap-1 text-xs text-[#FF3B6B] hover:underline" data-testid={`download-broll-${job.id}`}>
        <Download size={12} /> Download MP4
      </a>
    </div>
  );
}
